import React, { useState } from "react";

const projects = [
  {
    title: "Dr. B. Mini Devi – Academic Website",
    image: "/assets/og-img.jpg",
    description:
      "An academic website dedicated to Dr. B. Mini Devi showcasing her academic journey.",
    status: "Academic Web Platform",
    link: "https://bminidevi.com",
  },
  {
    title: "Smart Research Alerts",
    image: "/assets/sra-thumbnail.png",
    description:
      "Personalized weekly updates to simplify literature discovery.",
    video: "/assets/sra-demo.mp4",
    status: "AI-Powered Alert System",
    paperStatus: "📄 Associated manuscript under peer review.",
  },
  {
    title: "Research Cartographer",
    image: "/assets/RC-thumbnail.png",
    description:
      "A framework that maps research themes using unsupervised, explainable knowledge discovery.",
    video: "/assets/RC-demo.mp4",
    status: "Computational Framework",
    paperStatus: "📄 Associated manuscript under peer review.",
  },
  {
    title: "Semantic OPAC",
    image: "/assets/semantic-opac.png",
    description:
      "Towards concept-based exploration in digital library systems.",
    type: "concept",
    status: "AI-Powered Research System",
  },
  {
    title: "iRis",
    image: "/assets/iRis-VRA.png",
    description:
      "A system that enables intelligent reference services using AI-driven, semantic knowledge discovery.",
    status: "Ongoing Research",
  },
];

export default function Projects() {
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeConcept, setActiveConcept] = useState(null);

  return (
    <section
      id="projects"
      className="scroll-mt-24 py-20 px-6 bg-white text-gray-800"
      data-aos="fade-up"
    >
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-10 transition-colors duration-300 hover:text-green-700">
          Research & Intelligent Systems
        </h2>

        <div className="w-full flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="relative bg-white shadow-xl rounded-xl overflow-hidden max-w-sm w-full transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl hover:bg-gray-50"
              >

                <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="object-contain h-full w-full"
                  />
                </div>

                <div className="p-5 text-left">
                  {project.status && (
                    <span className="inline-flex items-center mb-2 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                      {project.status === "Ongoing Research" && (
                        <span className="pulse-dot"></span>
                      )}
                      {project.status}
                    </span>
                  )}

                  <h3 className="text-lg font-semibold text-black transition-colors duration-300 hover:text-green-700 mb-2">
                    {project.title}
                  </h3>

                  <p className="text-gray-700 text-sm mb-2">
                    {project.description}
                  </p>

                  {project.paperStatus && (
                    <p className="text-xs text-gray-500 mb-3">
                      {project.paperStatus}
                    </p>
                  )}

                  <div className="flex items-center gap-6 mt-2">
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-emerald-600 hover:underline transition duration-300"
                      >
                        Visit Website →
                      </a>
                    )}

                    {project.video && (
                      <button
                        onClick={() => setActiveVideo(project.video)}
                        className="text-sm font-medium text-emerald-600 hover:underline transition duration-300"
                      >
                        View More →
                      </button>
                    )}

                    {project.type === "concept" && (
                      <button
                        onClick={() => setActiveConcept(project.title)}
                        className="text-sm font-medium text-emerald-600 hover:underline transition duration-300"
                      >
                        View More →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Video Modal */}
        {activeVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
            <div className="relative w-full max-w-3xl">
              <video
                src={activeVideo}
                controls
                autoPlay
                className="w-full rounded-xl shadow-2xl"
              />
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full px-3 py-1 hover:bg-red-700"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Concept Modal */}
        {activeConcept && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
            <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl p-8 text-left">
              <h3 className="text-2xl font-bold text-emerald-700 mb-2">
                Semantic OPAC
              </h3>

              <p className="text-gray-600 italic mb-4">
                Transforming Traditional OPAC into Intelligent Knowledge Systems
              </p>

              <p className="text-gray-700 mb-3">
                Semantic OPAC reimagines traditional catalogue systems by enabling concept-driven exploration instead of keyword dependency.
              </p>

              <p className="text-gray-700 mb-6">
                The framework focuses on contextual relevance, adaptive discovery, and structured knowledge navigation.
              </p>

              <p className="text-sm text-gray-500 mb-4">
                Full technical details are available in the arXiv preprint; this work was presented at Intellib 2026.
              </p>

              <a
                href="https://arxiv.org/abs/2604.01262"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-emerald-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-emerald-700 transition duration-300"
              >
                📄 View Preprint
              </a>

              <button
                onClick={() => setActiveConcept(null)}
                className="absolute top-3 right-4 text-gray-600 hover:text-red-600 text-lg"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
